/**
 * Validation and conditional visibility, as the designer holds them.
 *
 * Two features that share one problem: Formily expresses both as JSON that is
 * easy to *run* and impossible to *edit back*. `x-validator` is a loose array
 * whose entries may be strings, objects or functions, and `x-reactions` carries
 * a JavaScript expression string. Reading either back into a form somebody can
 * click on means guessing, and guessing wrong means silently rewriting a rule
 * that was working.
 *
 * So this file keeps a **structured** copy of what the designer authored, under
 * its own key, and generates the Formily shape from it:
 *
 *     x-designer-rules: { validators: [...], visibleWhen: {...} }   <- edited
 *     x-validator:      [...]                                       <- generated
 *     x-reactions:      [...]                                       <- generated
 *
 * The structured copy is the source of truth for editing; the generated keys are
 * the source of truth for running. That is the same split the process engine
 * makes between `condition_json` and `conditions.describe`, and it buys the same
 * thing: a rule can always be read back as a sentence.
 *
 * **A field whose `x-validator` or `x-reactions` we did not write is left
 * completely alone.** The classic antd-4 designer can author both, and 54 stored
 * schemas came from it. Overwriting one would be exactly the failure
 * `schema.ts` exists to prevent -- see `foreignValidator` / `foreignReactions`,
 * which is what the inspector shows a read-only notice for.
 */

/** Where a rule's structured form lives on the node. */
export const RULES_KEY = "x-designer-rules";

export type ValidatorKind =
  | "min"
  | "max"
  | "minLength"
  | "maxLength"
  | "pattern"
  | "format";

export type Validator = {
  kind: ValidatorKind;
  /** A number for min/max/length, a regex source for pattern, a name for format. */
  value: string;
  /** What the person filling the form is told. Blank = Formily's own wording. */
  message?: string;
};

/** One leaf of "show this field only when…". Deliberately a single clause. */
export type VisibleWhen = {
  /** The `key` of another field in the same form. */
  field: string;
  op: "eq" | "ne" | "in" | "not_in" | "filled" | "empty" | "gt" | "lt";
  value?: string | string[];
};

export type Rules = {
  validators: Validator[];
  visibleWhen: VisibleWhen | null;
};

export const EMPTY_RULES: Rules = { validators: [], visibleWhen: null };

/** The formats Formily validates out of the box and we are happy to promise. */
export const FORMATS = [
  { value: "email", label: "Email address" },
  { value: "url", label: "Web address" },
  { value: "integer", label: "Whole number" },
  { value: "number", label: "Number" },
  { value: "phone", label: "Phone number" },
];

/**
 * Ready-made patterns, because a regex box on its own is a trap.
 *
 * An administrator who can write `^[A-Z]{2}-\d{4}$` does not need help; one who
 * cannot will paste something from a search result and find out it is wrong when
 * a permit is refused in the field. The presets cover what these forms actually
 * ask for, and the box stays for the people who do know.
 */
export const PATTERN_PRESETS = [
  { value: "^[A-Za-z0-9-]+$", label: "Letters, numbers and dashes only" },
  { value: "^\\d+$", label: "Digits only" },
  { value: "^[A-Z]{2}\\d{4}$", label: "Two letters then four digits" },
  { value: "^.{1,}$", label: "Anything, but not blank" },
];

/** Which validators make sense for a component — offering min/max on a text
 *  box is how a rule that can never fire gets written. */
export const validatorsFor = (component: string): ValidatorKind[] => {
  if (component === "NumberPicker" || component === "Rate") {
    return ["min", "max"];
  }
  if (component === "Input" || component === "Input.TextArea") {
    return ["minLength", "maxLength", "pattern", "format"];
  }
  return [];
};

export const VALIDATOR_LABEL: Record<ValidatorKind, string> = {
  min: "At least",
  max: "At most",
  minLength: "Shortest allowed",
  maxLength: "Longest allowed",
  pattern: "Must match",
  format: "Must be a",
};

/* ------------------------------------------------------------------ parse */

const asRules = (value: any): Rules => {
  const validators = Array.isArray(value?.validators)
    ? value.validators
        .filter((v: any) => v && typeof v.kind === "string")
        .map((v: any) => ({
          kind: v.kind as ValidatorKind,
          value: String(v.value ?? ""),
          message: typeof v.message === "string" ? v.message : "",
        }))
    : [];
  const raw = value?.visibleWhen;
  const visibleWhen =
    raw && typeof raw.field === "string" && typeof raw.op === "string"
      ? { field: raw.field, op: raw.op, value: raw.value }
      : null;
  return { validators, visibleWhen };
};

export const readRules = (raw: Record<string, any>): Rules =>
  asRules(raw?.[RULES_KEY]);

/**
 * True when the node carries a validator this designer did not write.
 *
 * `makeField` seeds `"x-validator": []`, and the classic designer writes the
 * same empty array on fields with no rules, so an empty array is *not* foreign —
 * treating it as such would put a read-only notice on almost every field in the
 * repository.
 */
export const foreignValidator = (raw: Record<string, any>): boolean => {
  if (raw?.[RULES_KEY]) return false;
  const v = raw?.["x-validator"];
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

export const foreignReactions = (raw: Record<string, any>): boolean => {
  if (raw?.[RULES_KEY]) return false;
  const r = raw?.["x-reactions"];
  if (r === undefined || r === null) return false;
  if (Array.isArray(r)) return r.length > 0;
  return true;
};

/* -------------------------------------------------------------- generate */

/** One structured validator as the plain-JSON descriptor Formily runs. */
const toFormilyValidator = (v: Validator): Record<string, any> | null => {
  const message = v.message?.trim() || undefined;
  switch (v.kind) {
    case "min":
      return Number.isNaN(Number(v.value))
        ? null
        : { minimum: Number(v.value), message };
    case "max":
      return Number.isNaN(Number(v.value))
        ? null
        : { maximum: Number(v.value), message };
    case "minLength":
      return Number.isNaN(Number(v.value))
        ? null
        : { minLength: Number(v.value), message };
    case "maxLength":
      return Number.isNaN(Number(v.value))
        ? null
        : { maxLength: Number(v.value), message };
    case "pattern":
      return v.value ? { pattern: v.value, message } : null;
    case "format":
      return v.value ? { format: v.value, message } : null;
    default:
      return null;
  }
};

/** The JS expression Formily evaluates for a visibility rule.
 *
 *  Generated, never typed. That is the whole reason the structured form exists:
 *  a hand-written expression could not be read back into the builder, could not
 *  be described in words, and would be a scripting language in a rule — which
 *  this product has deliberately refused everywhere else.
 */
const toExpression = (rule: VisibleWhen): string => {
  const dep = "$deps[0]";
  const lit = (v: unknown) => JSON.stringify(String(v ?? ""));
  const list = (v: unknown) =>
    JSON.stringify((Array.isArray(v) ? v : [v]).map((x) => String(x ?? "")));
  // `$deps[0]` may be a scalar or an array (a checkbox group), so equality is
  // written to mean "this is the answer" in both shapes. Without the array arm
  // a rule on a multiple-choice question is silently always false.
  const has = `(Array.isArray(${dep}) ? ${dep}.map(String) : [String(${dep} ?? "")])`;
  switch (rule.op) {
    case "eq":
      return `{{ ${has}.includes(${lit(rule.value)}) }}`;
    case "ne":
      return `{{ !${has}.includes(${lit(rule.value)}) }}`;
    case "in":
      return `{{ ${has}.some(v => ${list(rule.value)}.includes(v)) }}`;
    case "not_in":
      return `{{ !${has}.some(v => ${list(rule.value)}.includes(v)) }}`;
    case "filled":
      return `{{ ${dep} !== undefined && ${dep} !== null && ${dep} !== "" && !(Array.isArray(${dep}) && ${dep}.length === 0) }}`;
    case "empty":
      return `{{ ${dep} === undefined || ${dep} === null || ${dep} === "" || (Array.isArray(${dep}) && ${dep}.length === 0) }}`;
    case "gt":
      return `{{ Number(${dep}) > ${Number(rule.value) || 0} }}`;
    case "lt":
      return `{{ Number(${dep}) < ${Number(rule.value) || 0} }}`;
    default:
      return "{{ true }}";
  }
};

/**
 * Write the generated keys onto a raw schema node, in place.
 *
 * Called from `serialise`. Removing a rule removes the generated key rather
 * than leaving an empty one behind: `"x-reactions": []` is harmless but it is
 * also indistinguishable from a rule somebody deleted by mistake.
 */
export const applyRules = (out: Record<string, any>, rules: Rules): void => {
  const clean: Rules = {
    validators: rules.validators.filter((v) => toFormilyValidator(v) !== null),
    visibleWhen: rules.visibleWhen,
  };

  if (clean.validators.length === 0 && !clean.visibleWhen) {
    delete out[RULES_KEY];
  } else {
    out[RULES_KEY] = clean;
  }

  if (clean.validators.length > 0) {
    out["x-validator"] = clean.validators
      .map(toFormilyValidator)
      .filter(Boolean);
  } else if (RULES_KEY in out || Array.isArray(out["x-validator"])) {
    // Only clear a validator array WE could have written. A foreign one is
    // never reached here, because the inspector refuses to edit those fields.
    out["x-validator"] = [];
  }

  if (clean.visibleWhen) {
    out["x-reactions"] = [
      {
        dependencies: [clean.visibleWhen.field],
        fulfill: {
          state: {
            visible: toExpression(clean.visibleWhen),
          },
        },
      },
    ];
  } else if (Array.isArray(out["x-reactions"])) {
    delete out["x-reactions"];
  }
};

/* ------------------------------------------------------------- describe */

const OP_WORDS: Record<VisibleWhen["op"], string> = {
  eq: "is",
  ne: "is not",
  in: "is one of",
  not_in: "is none of",
  filled: "is answered",
  empty: "is blank",
  gt: "is more than",
  lt: "is less than",
};

/** A visibility rule as a sentence, for the inspector and the canvas chip. */
export const describeVisibleWhen = (
  rule: VisibleWhen | null,
  label: (key: string) => string
): string => {
  if (!rule) return "";
  const words = OP_WORDS[rule.op] ?? rule.op;
  if (rule.op === "filled" || rule.op === "empty") {
    return `'${label(rule.field)}' ${words}`;
  }
  const listed = (Array.isArray(rule.value) ? rule.value : [rule.value])
    .filter((v) => v !== undefined && v !== "")
    .join(", ");
  return `'${label(rule.field)}' ${words} ${listed}`;
};

/** A validator as a sentence. */
export const describeValidator = (v: Validator): string => {
  switch (v.kind) {
    case "min":
      return `at least ${v.value}`;
    case "max":
      return `at most ${v.value}`;
    case "minLength":
      return `at least ${v.value} characters`;
    case "maxLength":
      return `at most ${v.value} characters`;
    case "pattern":
      return `matching ${v.value}`;
    case "format":
      return `a valid ${FORMATS.find((f) => f.value === v.value)?.label ?? v.value}`;
    default:
      return v.kind;
  }
};
